import { metricPercent, riskLabel } from '@/lib/agentPresentation';
import SubscriptionStatus from '@/components/feature/SubscriptionStatus';
import FollowActionDialog from '@/components/feature/FollowActionDialog';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType, useSearchParams } from 'react-router-dom';
import { decisionPresentation, shortAgentLabel } from '@/lib/decisionPresentation';
import { readDecisionListView, restoreDecisionListView, saveDecisionListView, validDecisionListView } from '@/lib/decisionListNavigation';
import { useAuth } from '@/contexts/AuthContext';
import EmptyState from '@/components/base/EmptyState';
import LockedPrompt from '@/components/feature/LockedPrompt';
import {
  followStatusConfig,
  type BrokerAccount,
  type FollowRecord,
} from '@/lib/brokerAccounts';
import {
  forexApi,
  toBrokerAccount,
  toFollowRecord,
  toUiAgentId,
  executionStatusLabel,
  verifiedPnlText,
  type PlatformAgentDTO,
  type AiDecisionDTO,
} from '@/services/forexApi';

// ═══════════════════════════════════════
//  Labels & Colors
// ═══════════════════════════════════════
const strategyLabels: Record<string, string> = {
  trend: '趋势跟踪', scalping: '高频剥头皮', grid: '网格策略',
  arbitrage: '套利策略', mean_reversion: '均值回归',
  trend_following: '趋势跟踪', breakout: '突破策略',
};

const riskColors: Record<string, { bg: string; text: string; label: string }> = {
  conservative: { bg: 'bg-blue-50', text: 'text-blue-500', label: '稳健' },
  moderate: { bg: 'bg-yellow-50', text: 'text-yellow-600', label: '均衡' },
  aggressive: { bg: 'bg-red-50', text: 'text-red-500', label: '激进' },
  standard: { bg: 'bg-blue-50', text: 'text-blue-500', label: '标准' },
  strict: { bg: 'bg-yellow-50', text: 'text-yellow-600', label: '严格' },
};

const agentStatusStyles: Record<string, { bg: string; text: string; label: string }> = {
  available: { bg: 'bg-green-50', text: 'text-green-600', label: '可跟随' },
  following: { bg: 'bg-indigo-50', text: 'text-indigo-600', label: '跟单中' },
  unavailable: { bg: 'bg-gray-50', text: 'text-gray-400', label: '暂不可跟随' },
  validating: { bg: 'bg-amber-50', text: 'text-amber-700', label: '验证中' },
};

const decisionActionColors: Record<string, { bg: string; text: string }> = {
  BUY: { bg: 'bg-green-50', text: 'text-green-600' },
  SELL: { bg: 'bg-red-50', text: 'text-red-600' },
  HOLD: { bg: 'bg-purple-50', text: 'text-purple-600' },
  CLOSE: { bg: 'bg-amber-50', text: 'text-amber-600' },
};

type TabType = 'agents' | 'my-copy' | 'decisions';

interface AgentCardData {
  id: string;
  is_validation?: boolean;
  uiId: number | string;
  name: string;
  description: string;
  strategy_type: string;
  strategy_timeframes?: PlatformAgentDTO['strategy_timeframes'];
  risk_level: string;
  total_return: number | null;
  win_rate: number | null;
  max_drawdown: number | null;
  sharpe_ratio: number;
  running_time: string;
  status: 'available' | 'following' | 'unavailable' | 'validating';
  avatar_color: string;
  currency_pairs: string[];
  follower_count: number | null;
  trade_count: number;
  last_decision: { pair: string; action: string | null; confidence: number | null; time: string } | null;
}

interface DecisionItem {
  summary?: string;
  label: string;
  tone: string;
  id: string;
  pair: string;
  action: string | null;
  confidence: number | null;
  agent_name: string;
  time: string;
  exec_status: string;
  pnl: number | null;
  pnl_currency?: string | null;
  show_pnl: boolean;
  record_type?: string;
}

interface ExecutionItem {
  id: string;
  pair: string;
  direction: string;
  lot: number | null;
  pnl: number | null;
  pnl_currency?: string | null;
  status: string;
  time: string;
}

const agentColors = ['#6366F1', '#10B981', '#F59E0B', '#8B5CF6', '#0EA5E9'];
const AI_DECISION_REQUEST_TIMEOUT_MS = 30_000;

async function requestWithDeadline<T>(
  request: (signal: AbortSignal) => Promise<T>, timeoutMs = 12_000, parentSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (parentSignal?.aborted) controller.abort();
  parentSignal?.addEventListener('abort', abort, { once: true });
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await request(controller.signal);
  } finally {
    window.clearTimeout(timeout);
    parentSignal?.removeEventListener('abort', abort);
  }
}

function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div role="alert" className="bg-white rounded-2xl p-4 text-sm text-gray-600">
    <p>{message}</p>
    <button type="button" onClick={onRetry} className="mt-3 text-indigo-600 font-semibold">重试</button>
  </div>;
}

function formatSymbol(symbol: string): string {
  if (!symbol) return '-';
  if (symbol.includes('/')) return symbol;
  if (symbol.length === 6) return `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  if (symbol === 'XAUUSD') return 'XAU/USD';
  return symbol;
}

function formatTime(value: string): string {
  const time = new Date(value);
  if (Number.isNaN(time.getTime())) return value;
  return time.toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit' });
}

function buildAgentList(
  platformAgents: PlatformAgentDTO[],
  follows: FollowRecord[],
  decisions: AiDecisionDTO[],
): AgentCardData[] {
  const runningAgentIds = new Set(
    follows.filter((follow) => follow.followStatus === 'running').map((follow) => follow.agentId),
  );
  const decisionByAgent = decisions.reduce<Record<string, AiDecisionDTO>>((acc, decision) => {
    const uiId = toUiAgentId(decision.agent_id);
    if (!acc[String(uiId)]) acc[String(uiId)] = decision;
    return acc;
  }, {});

  return platformAgents.map((agent, index) => {
    const uiId = toUiAgentId(agent.agent_id);
    const decision = decisionByAgent[String(uiId)];
    return {
      id: agent.agent_id,
      is_validation: agent.is_validation,
      uiId,
      name: agent.name,
      description: `${strategyLabels[agent.strategy_type] || agent.strategy_type} · 支持 ${agent.supported_symbols.map(formatSymbol).join('、')}`,
      strategy_type: agent.strategy_type,
      strategy_timeframes: agent.strategy_timeframes,
      risk_level: agent.risk_level,
      total_return: agent.total_return,
      win_rate: agent.win_rate,
      max_drawdown: agent.max_drawdown,
      sharpe_ratio: agent.sharpe_ratio || 0,
      running_time: agent.running_time || '-',
      status: agent.is_validation ? 'validating' : runningAgentIds.has(uiId) ? 'following' : agent.status,
      avatar_color: agentColors[index % agentColors.length],
      currency_pairs: agent.supported_symbols.map(formatSymbol),
      follower_count: agent.follower_count,
      trade_count: 0,
      last_decision: !agent.is_validation && decision
        ? { pair: formatSymbol(decision.symbol), action: decision.action, confidence: decision.confidence, time: formatTime(decision.created_at) }
        : null,
    };
  });
}

// ═══════════════════════════════════════
//  智能体列表分区
// ═══════════════════════════════════════
function AgentsSection() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [platformAgents, setPlatformAgents] = useState<PlatformAgentDTO[]>([]);
  const [follows, setFollows] = useState<FollowRecord[]>([]);
  const [recentDecisions, setRecentDecisions] = useState<AiDecisionDTO[]>([]);
  const [hasConnected, setHasConnected] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [partialError, setPartialError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const agents = buildAgentList(platformAgents, follows, recentDecisions);

  useEffect(() => {
    if (!accessToken) {
      setPlatformAgents([]);
      setHasConnected(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let requestId = 0;
    let inFlight: AbortController | null = null;
    let lastRefreshAt = 0;

    function refresh() {
      inFlight?.abort();
      inFlight = new AbortController();
      const signal = inFlight.signal;
      lastRefreshAt = Date.now();
      const current = ++requestId;
      const active = () => !cancelled && current === requestId;
      setLoading(true);
      setError(false);
      setPartialError(false);
      setHasConnected(null);
      setPlatformAgents([]);
      setFollows([]);
      setRecentDecisions([]);
      void requestWithDeadline(async (deadlineSignal) => {
        const official = await forexApi.listPlatformAgents(accessToken, deadlineSignal);
        const validation = await forexApi.listValidationAgents(accessToken, deadlineSignal).catch(() => { if (active()) setPartialError(true); return []; });
        return [...official, ...validation];
      }, 12_000, signal)
        .then((data) => { if (active()) setPlatformAgents(data); })
        .catch(() => { if (active()) setError(true); })
        .finally(() => { if (active()) setLoading(false); });
      void Promise.all([
        requestWithDeadline((deadlineSignal) => forexApi.listFollowerAccounts(accessToken, deadlineSignal), 12_000, signal),
        requestWithDeadline((deadlineSignal) => forexApi.listFollowRelations(accessToken, deadlineSignal), 12_000, signal),
      ]).then(([accountDtos, relationDtos]) => {
        if (!active()) return;
        const accounts = accountDtos.map(toBrokerAccount);
        setHasConnected(accounts.some((account) => account.accountStatus === 'connected'));
        setFollows(relationDtos.map((relation) => toFollowRecord(relation, accounts)));
      }).catch(() => { if (active()) setPartialError(true); });
      void requestWithDeadline((deadlineSignal) => forexApi.listAiDecisions(accessToken, 1, 20, {}, deadlineSignal), AI_DECISION_REQUEST_TIMEOUT_MS, signal)
        .then((page) => { if (active()) setRecentDecisions(page.items); })
        .catch(() => { if (active()) setPartialError(true); });
    }

    refresh();
    const onVisibility = () => { if (!document.hidden && Date.now() - lastRefreshAt >= 30_000) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      inFlight?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [accessToken, retryKey]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {[1, 2, 3].map((item) => (
          <div key={item} className="bg-white rounded-2xl h-56 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {error && <LoadError message="智能体暂时无法加载，请稍后重试。" onRetry={() => setRetryKey((key) => key + 1)} />}
      {partialError && <p role="status" className="text-xs text-amber-700">部分账户、跟单或决策信息暂不可用，请稍后重试。</p>}
      {!error && agents.length === 0 && <EmptyState icon="ri-robot-2-line" title="暂无可浏览智能体" subtitle="平台暂未提供可跟随的智能体" />}
      {hasConnected === false && (
        <div className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
              <i className="ri-link-unlink-m text-amber-500 text-lg" />
            </div>
            <div className="flex-1">
              <p className="text-gray-900 font-semibold text-sm">绑定交易账户后开启跟单</p>
              <p className="text-gray-400 text-xs mt-0.5">绑定交易账户后才能跟随 AI 智能体</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/broker')}
            className="w-full mt-3 py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 text-white"
            style={{ background: 'linear-gradient(135deg, #6366F1, #7C3AED)' }}>
            <i className="ri-link-m" /> 去绑定账户
          </button>
        </div>
      )}

      {agents.map((agent) => {
        const risk = { ...(riskColors[agent.risk_level] || { bg: 'bg-gray-100', text: 'text-gray-500' }), label: riskLabel(agent.risk_level) };
        const statusCfg = agentStatusStyles[agent.status] || agentStatusStyles.available;

        return (
          <div key={`${agent.id}:${agent.is_validation ? 'validation' : 'official'}`} className="bg-white rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
            <div className="h-1 w-full" style={{ background: agent.avatar_color }} />
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
                  style={{ background: agent.avatar_color }}>
                  <i className="ri-robot-2-fill text-white text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-bold text-gray-900 text-sm truncate">{agent.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${risk.bg} ${risk.text}`}>
                      {risk.label}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${statusCfg.bg} ${statusCfg.text}`}>
                      {statusCfg.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="bg-indigo-50 text-indigo-500 text-[10px] px-2 py-0.5 rounded-md">
                      {strategyLabels[agent.strategy_type]}
                    </span>
                    <span className="text-gray-400 text-[10px] truncate">
                      {agent.currency_pairs.slice(0, 3).join(' · ')}
                    </span>
                  </div>
                  <div className="text-gray-500 text-[10px] mt-1">
                    {agent.is_validation ? '验证周期' : '生效周期'}：{agent.strategy_timeframes?.length
                      ? agent.strategy_timeframes.map((item) => `${formatSymbol(item.symbol)} · ${item.timeframe}`).join('、') : '—'}
                  </div>
                </div>
              </div>

              {/* Stats */}
              {agent.is_validation ? <p className="mt-4 border-t border-gray-50 pt-3 text-xs text-amber-700">验证阶段不展示正式收益和胜率。</p> :
              <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-gray-50">
                <div className="text-center">
                  <p className={`text-sm font-bold ${agent.total_return == null ? 'text-gray-400' : agent.total_return >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {metricPercent(agent.total_return, true)}
                  </p>
                  <p className="text-gray-400 text-[10px]">总收益</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900">{metricPercent(agent.win_rate)}</p>
                  <p className="text-gray-400 text-[10px]">胜率</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900">{metricPercent(agent.max_drawdown)}</p>
                  <p className="text-gray-400 text-[10px]">最大回撤</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-bold text-gray-900">暂未提供</p>
                  <p className="text-gray-400 text-[10px]">跟随人数</p>
                </div>
              </div>}

              {!agent.is_validation && [agent.total_return, agent.win_rate, agent.max_drawdown].some(value => value == null) && <p className="mt-2 text-xs text-gray-400">业绩数据暂未提供</p>}
              {/* Last decision */}
              {agent.last_decision && (
                <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-50">
                  <span className="text-gray-400 text-[10px]">最近决策</span>
                  <span className="text-gray-900 text-xs font-medium">{agent.last_decision.pair}</span>
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                    decisionActionColors[agent.last_decision.action]?.bg || decisionActionColors.HOLD.bg
                  } ${decisionActionColors[agent.last_decision.action]?.text || decisionActionColors.HOLD.text}`}>
                    {agent.last_decision.action || '未形成动作'}
                  </span>
                  <span className="text-gray-400 text-[10px] ml-auto">{agent.last_decision.time}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigate(`/agent/${agent.id}${agent.is_validation ? '?validation=1' : ''}`)}
                  className="flex-1 bg-gray-50 text-gray-600 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-1">
                  <i className="ri-bar-chart-line text-sm" /> 查看详情
                </button>
                {hasConnected === null ? (
                  <button disabled className="flex-1 bg-gray-100 text-gray-400 text-sm font-medium py-2.5 rounded-xl">
                    账户状态暂不可用
                  </button>
                ) : !hasConnected ? (
                  <button
                    onClick={() => navigate('/broker')}
                    className="flex-1 bg-amber-50 text-amber-600 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-1">
                    <i className="ri-link-m text-sm" /> 先绑定账户
                  </button>
                ) : agent.status === 'following' ? (
                  <button
                    onClick={() => navigate(`/copy-config?agentId=${agent.uiId}${agent.is_validation ? '&validation=1' : ''}`)}
                    className="flex-1 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-1"
                    style={{ background: `${agent.avatar_color}14`, color: agent.avatar_color }}>
                    <i className="ri-settings-4-line text-sm" /> 管理跟单
                  </button>
                ) : agent.status === 'unavailable' ? (
                  <button disabled className="flex-1 bg-gray-100 text-gray-400 text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-1">
                    <i className="ri-forbid-line text-sm" /> 暂不可跟随
                  </button>
                ) : (
                  <button
                    onClick={() => navigate(`/copy-config?agentId=${agent.uiId}${agent.is_validation ? '&validation=1' : ''}`)}
                    className="flex-1 text-white text-sm font-medium py-2.5 rounded-xl flex items-center justify-center gap-1"
                    style={{ background: `linear-gradient(135deg, ${agent.avatar_color}, ${agent.avatar_color}cc)` }}>
                    <i className="ri-add-line text-sm" /> 跟随
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════
//  我的跟单分区
// ═══════════════════════════════════════
function MyCopySection() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [follows, setFollows] = useState<FollowRecord[]>([]);
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [agents, setAgents] = useState<PlatformAgentDTO[]>([]);
  const [executions, setExecutions] = useState<ExecutionItem[]>([]);
  const [hasConnected, setHasConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [partialError, setPartialError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const [executionsLoading, setExecutionsLoading] = useState(true);
  const [executionsError, setExecutionsError] = useState(false);
  const [executionsRetry, setExecutionsRetry] = useState(0);

  // Modal states
  const actionLock = useRef(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [authorizationRevision, setAuthorizationRevision] = useState(0);
  const [pauseTarget, setPauseTarget] = useState<FollowRecord | null>(null);
  const [stopTarget, setStopTarget] = useState<FollowRecord | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'warning' } | null>(null);

  useEffect(() => {
    if (!accessToken) {
      setFollows([]);
      setAccounts([]);
      setAgents([]);
      setExecutions([]);
      setHasConnected(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let requestId = 0;
    let inFlight: AbortController | null = null;
    let lastRefreshAt = 0;

    function refresh() {
      inFlight?.abort();
      inFlight = new AbortController();
      const signal = inFlight.signal;
      lastRefreshAt = Date.now();
      const current = ++requestId;
      const active = () => !cancelled && current === requestId;
      setLoading(true);
      setError(false);
      setPartialError(false);
      setAgents([]);
      void Promise.all([
        requestWithDeadline((deadlineSignal) => forexApi.listFollowerAccounts(accessToken, deadlineSignal), 12_000, signal),
        requestWithDeadline((deadlineSignal) => forexApi.listFollowRelations(accessToken, deadlineSignal), 12_000, signal),
      ]).then(([accountDtos, relationDtos]) => {
        const nextAccounts = accountDtos.map(toBrokerAccount);
        const nextFollows = relationDtos
          .map((relation) => toFollowRecord(relation, nextAccounts))
          .filter((follow) => follow.followStatus !== 'stopped' && follow.followStatus !== 'agent_unavailable');
        if (active()) {
          setAccounts(nextAccounts);
          setFollows(nextFollows);
          setHasConnected(nextAccounts.some((account) => account.accountStatus === 'connected'));
        }
      }).catch(() => { if (active()) setError(true); })
        .finally(() => { if (active()) setLoading(false); });
      void requestWithDeadline((deadlineSignal) => forexApi.listPlatformAgents(accessToken, deadlineSignal), 12_000, signal)
        .then((agentDtos) => { if (active()) setAgents(agentDtos); })
        .catch(() => { if (active()) setPartialError(true); });

    }

    refresh();
    const onVisibility = () => { if (!document.hidden && Date.now() - lastRefreshAt >= 30_000) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      inFlight?.abort();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [accessToken, retryKey]);

  useEffect(() => { setExecutions([]); setExecutionsError(false); }, [accessToken]);

  useEffect(() => {
    if (!accessToken) { setExecutionsLoading(false); return; }
    let cancelled = false;
    let requestId = 0;
    let inFlight: AbortController | null = null;
    let lastRefreshAt = 0;
    function refresh() {
      inFlight?.abort();
      inFlight = new AbortController();
      const signal = inFlight.signal;
      lastRefreshAt = Date.now();
      const current = ++requestId;
      const active = () => !cancelled && current === requestId;
      setExecutionsLoading(true);
      setExecutionsError(false);
      void requestWithDeadline(() => forexApi.listClosedTradePositions(accessToken), 12_000, signal)
        .then((executionPage) => { if (active()) setExecutions(executionPage.items.slice(0, 5).map((execution) => ({
          id: execution.id,
          pair: formatSymbol(execution.symbol),
          direction: execution.action,
          lot: execution.lot,
          pnl: execution.pnl,
          pnl_currency: execution.pnl_currency,
          status: execution.lifecycle_status,
          time: formatTime(execution.opened_at),
        }))); })
        .catch(() => { if (active()) setExecutionsError(true); })
        .finally(() => { if (active()) setExecutionsLoading(false); });
    }
    refresh();
    const onVisibility = () => { if (!document.hidden && Date.now() - lastRefreshAt >= 30_000) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { cancelled = true; inFlight?.abort(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [accessToken, executionsRetry]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const getAgentName = (agentId: number | string) => {
    const agent = agents.find((item) => toUiAgentId(item.agent_id) === agentId);
    return agent?.name || `智能体 #${agentId}`;
  };

  const refreshFollows = async () => {
    if (!accessToken) return;
    const relationDtos = await forexApi.listFollowRelations(accessToken);
    setFollows(
      relationDtos
        .map((relation) => toFollowRecord(relation, accounts))
        .filter((follow) => follow.followStatus !== 'stopped' && follow.followStatus !== 'agent_unavailable'),
    );
  };

  const handleAction = async (action: 'pause' | 'stop') => {
    const target = action === 'pause' ? pauseTarget : stopTarget;
    if (!target || !accessToken || actionLock.current) return;
    actionLock.current = true; setActionBusy(true); setActionError('');
    try {
      if (action === 'pause') await forexApi.pauseFollowRelation(target.id, accessToken);
      else await forexApi.stopFollowRelation(target.id, accessToken);
      await refreshFollows();
      setAuthorizationRevision(n => n + 1);
      setPauseTarget(null); setStopTarget(null);
      setToast({ msg: action === 'pause' ? '跟随已暂停，请查看账户最新状态。' : '跟随已停止，账户保持连接；持仓结果请继续查看。', type: 'warning' });
    } catch {
      setPauseTarget(null); setStopTarget(null);
      setActionError('操作未完整完成，请刷新核对账户状态后再继续。');
      setRetryKey(n => n + 1);
    } finally { actionLock.current = false; setActionBusy(false); }
  };

  return (
    <div className="flex flex-col gap-4">
      {actionError && <div role="alert" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-800">{actionError}</div>}
      {loading && <p role="status" className="text-xs text-gray-400">正在加载跟单信息…</p>}
      {error && <LoadError message="跟单信息暂时无法加载，请稍后重试。" onRetry={() => setRetryKey((key) => key + 1)} />}
      {!loading && !error && follows.length === 0 && <EmptyState icon="ri-robot-2-line" title="暂无跟单"
        subtitle={hasConnected ? '浏览智能体列表，选择适合的策略开始跟随' : '请先绑定交易账户，再浏览智能体开启跟单'}
        primaryAction={{ label: hasConnected ? '浏览智能体' : '去绑定账户', onClick: () => navigate(hasConnected ? '/agent' : '/broker') }} />}
      {partialError && <p role="status" className="text-xs text-amber-700">智能体名称暂不可用。</p>}
      {/* Toast */}
      {toast && (
        <div className={`rounded-2xl p-4 border ${toast.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${toast.type === 'success' ? 'bg-green-100' : 'bg-amber-100'}`}>
              <i className={`text-lg ${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : 'ri-information-line text-amber-600'}`} />
            </div>
            <p className={`text-sm font-medium ${toast.type === 'success' ? 'text-green-800' : 'text-amber-800'}`}>{toast.msg}</p>
          </div>
        </div>
      )}

      {!loading && !error && follows.map((follow) => {
        const agentName = getAgentName(follow.agentId);
        const statusCfg = followStatusConfig[follow.followStatus];
        const isRunning = follow.followStatus === 'running';

        return (
          <div key={follow.id} className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
            <div className="h-1 w-full" style={{ background: '#6366F1' }} />
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: '#6366F1' }}>
                  <i className="ri-robot-2-fill text-white text-xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-gray-900 text-sm">{agentName}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                      {statusCfg.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-400 text-[10px]">账户 {follow.accountNo}</span>
                    <span className="text-gray-300 text-[10px]">·</span>
                    <span className="text-gray-400 text-[10px]">{follow.server}</span>
                  </div>
                </div>

              </div>

              {/* 已审核策略风险由服务端授权投影提供。 */}
              <div className="mt-4 pt-4 border-t border-gray-50">
                <p className="text-gray-400 text-[10px] mb-2">策略授权</p>
                <SubscriptionStatus relationId={follow.id} agentId={follow.agentId} revision={authorizationRevision} />
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-2 mt-4 pt-4 border-t border-gray-50">
                <button
                  onClick={() => navigate(`/copy-config?agentId=${follow.agentId}`)}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-11 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-xs font-medium active:bg-indigo-100">
                  <i className="ri-settings-4-line text-sm" /> 管理
                </button>
                {isRunning ? (
                  <button
                    disabled={actionBusy} onClick={() => setPauseTarget(follow)}
                    className="flex-1 flex items-center justify-center gap-1.5 min-h-11 py-2 rounded-xl bg-amber-50 text-amber-600 text-xs font-medium active:bg-amber-100">
                    <i className="ri-pause-circle-line text-sm" /> 暂停
                  </button>
                ) : follow.followStatus === 'paused_by_user' ? (
                  <button
                    disabled={actionBusy} onClick={() => navigate(`/copy-config?agentId=${encodeURIComponent(follow.agentId)}`)}
                    className="flex-1 flex items-center justify-center gap-1.5 min-h-11 py-2 rounded-xl bg-green-50 text-green-600 text-xs font-medium active:bg-green-100">
                    <i className="ri-play-circle-line text-sm" /> 查看并恢复
                  </button>
                ) : null}
                <button
                  disabled={actionBusy || follow.followStatus === 'status_unknown'} onClick={() => setStopTarget(follow)}
                  className="flex-1 flex items-center justify-center gap-1.5 min-h-11 py-2 rounded-xl bg-red-50 text-red-500 text-xs font-medium border border-red-100 active:bg-red-100">
                  <i className="ri-link-unlink-m text-sm" /> 停止
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* 最近执行记录 */}
      <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
        <div className="px-4 py-3 border-b border-gray-50 flex items-center justify-between">
          <span className="text-gray-900 font-semibold text-sm">最近 Broker 平仓</span>
          <span className="text-gray-400 text-[10px]">{(executionsLoading || executionsError) && executions.length === 0 ? '—' : `${executions.length} 条`}</span>
        </div>
        {executionsLoading && executions.length === 0 && <p role="status" className="px-4 py-6 text-center text-xs text-gray-400">正在加载平仓记录…</p>}
        {executionsError && <p role="alert" className="px-4 py-3 text-xs text-amber-700">{executions.length ? '平仓记录更新失败，显示上次结果。' : '平仓记录加载失败。'} <button onClick={() => setExecutionsRetry((key) => key + 1)}>重试平仓记录</button></p>}
        {!executionsLoading && !executionsError && executions.length === 0 && (
          <div className="px-4 py-6 text-center text-gray-400 text-xs">暂无已平仓记录</div>
        )}
        {executions.map((exec, idx) => (
          <div key={exec.id}>
            <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => navigate('/trade-history')}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${exec.direction === 'BUY' ? 'bg-green-50' : 'bg-red-50'}`}>
                <i className={`text-sm ${exec.direction === 'BUY' ? 'ri-arrow-up-line text-green-600' : 'ri-arrow-down-line text-red-600'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 text-sm font-medium">{exec.pair}</span>
                  <span className="text-gray-400 text-[10px]">{typeof exec.lot === 'number' ? `${exec.lot} lot` : '手数待回写'}</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-gray-400 text-[10px]">{exec.direction}</span>
                  <span className="text-gray-300 text-[10px]">·</span>
                  <span className="text-gray-400 text-[10px]">{exec.status === 'closed' ? 'Broker 已平仓' : executionStatusLabel(exec.status)}</span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className={`text-sm font-semibold ${typeof exec.pnl !== 'number' || !exec.pnl_currency ? 'text-gray-400' : exec.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {verifiedPnlText(exec.pnl, exec.pnl_currency)}
                </p>
                <p className="text-gray-400 text-[10px]">{exec.time}</p>
              </div>
            </div>
            {idx < executions.length - 1 && <div className="h-px mx-4 bg-gray-50" />}
          </div>
        ))}
      </div>

      {pauseTarget && <FollowActionDialog title="确认暂停跟随" busy={actionBusy}
        description={`账户尾号 ${pauseTarget.accountNo.slice(-4)}。暂停不等于平仓，已有持仓和执行结果仍需持续查看。`}
        onCancel={() => setPauseTarget(null)} onConfirm={() => void handleAction('pause')} />}
      {stopTarget && <FollowActionDialog title="确认停止跟随" busy={actionBusy}
        description={`账户尾号 ${stopTarget.accountNo.slice(-4)}。停止关系后账户保持连接，不代表持仓已平仓。存在退出义务时可能暂不能再次跟随。`}
        onCancel={() => setStopTarget(null)} onConfirm={() => void handleAction('stop')} />}

    </div>
  );
}

// ═══════════════════════════════════════
//  AI决策分区
// ═══════════════════════════════════════
function DecisionsSection() {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterParam = searchParams.get('decisionFilter');
  const filter = filterParam === 'following' || filterParam === '24h' ? filterParam : 'all';
  const restored = useRef(false);
  const [decisionDtos, setDecisionDtos] = useState<AiDecisionDTO[]>([]);
  const [agentDtos, setAgentDtos] = useState<PlatformAgentDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [partialError, setPartialError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const agentNameById = agentDtos.reduce<Record<string, string>>((acc, agent) => {
    acc[agent.agent_id] = agent.name;
    return acc;
  }, {});
  const decisions: DecisionItem[] = decisionDtos.map((decision) => {
    const presentation = decisionPresentation(decision);
    return {
      id: decision.id,
      pair: formatSymbol(decision.symbol),
      action: decision.action,
      confidence: decision.confidence,
      summary: presentation.reasonSummary,
      label: presentation.label,
      tone: presentation.tone,
      agent_name: agentNameById[decision.agent_id] || shortAgentLabel(decision.agent_id),
      time: formatTime(decision.created_at),
      exec_status: presentation.myCopyLabel,
      pnl: null,
      pnl_currency: null,
      show_pnl: false,
      record_type: decision.record_type,
    };
  });

  useEffect(() => {
    if (!accessToken) {
      setDecisionDtos([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    setPartialError(false);
    setDecisionDtos([]);
    setAgentDtos([]);
    const filters = filter === 'following' ? { followedOnly: true }
      : filter === '24h' ? { dateFrom: new Date(Date.now() - 86400000).toISOString() } : {};
    void requestWithDeadline((signal) => forexApi.listAiDecisions(accessToken, 1, 20, filters, signal), AI_DECISION_REQUEST_TIMEOUT_MS, controller.signal)
      .then((page) => { if (!cancelled) setDecisionDtos(page.items); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    void requestWithDeadline((signal) => forexApi.listPlatformAgents(accessToken, signal), 12_000, controller.signal)
      .then((agents) => { if (!cancelled) setAgentDtos(agents); })
      .catch(() => { if (!cancelled) setPartialError(true); });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [accessToken, filter, retryKey]);

  useEffect(() => {
    if (loading || error || restored.current) return;
    const view = validDecisionListView(location.state?.restoreDecisionList, user?.user_id)
      || (navigationType === 'POP' ? readDecisionListView(user?.user_id) : null);
    if (!view || (view.entryKey !== location.key && !location.state?.restoreDecisionList)
      || view.url !== `${location.pathname}${location.search}`) return;
    const frame = requestAnimationFrame(() => {
      restoreDecisionListView(view);
      restored.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, [loading, error, location, navigationType, user?.user_id]);

  function openDecision(id: string) {
    const view = user ? saveDecisionListView(user.user_id, location.key, `${location.pathname}${location.search}`, id) : null;
    navigate(`/ai-decisions/${encodeURIComponent(id)}`, { state: { decisionListReturn: view } });
  }

  const filters = [
    { key: 'all', label: '全部' },
    { key: 'following', label: '我跟随的' },
    { key: '24h', label: '近24小时' },
  ] as const;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        {filters.map((f) => (
          <button key={f.key} onClick={() => {
            const next = new URLSearchParams(searchParams);
            next.set('tab', 'decisions');
            next.set('decisionFilter', f.key);
            restored.current = true;
            setSearchParams(next, { replace: true, state: null });
          }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              filter === f.key ? 'bg-indigo-50 text-indigo-600 border border-indigo-200' : 'bg-white text-gray-500 border border-gray-200'
            }`}>{f.label}</button>
        ))}
      </div>

      {error && !loading && <LoadError message="AI 决策暂时无法加载，请稍后重试。" onRetry={() => setRetryKey((key) => key + 1)} />}
      {partialError && !error && <p role="status" className="text-xs text-amber-700">智能体名称或执行摘要暂不可用。</p>}

      {loading && (
        <div className="flex flex-col gap-3">
          <p role="status" className="text-xs text-gray-500">正在加载 AI 决策…</p>
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-white rounded-2xl h-32 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && !error && decisions.length === 0 && (
        <EmptyState
          icon="ri-flashlight-line"
          title="暂无 AI 决策"
          subtitle="该筛选条件下暂无可查看的 AI 决策"
        />
      )}

      <div className="flex flex-col gap-3">
        {!loading && decisions.map((d) => {
          return (
            <div key={d.id} id={`decision-${d.id}`} className="bg-white rounded-2xl p-4" style={{ boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 font-bold text-sm">{d.pair}</span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${d.tone}`}>{d.label}</span>
                </div>
                <span className="text-gray-400 text-[10px]">{d.time}</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-robot-2-line text-gray-400 text-xs" />
                <span className="text-gray-500 text-xs">{d.agent_name}</span>
                {d.record_type === 'legacy_decision' && <span className="text-gray-400 text-[10px]">历史决策</span>}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {d.summary ? <span className="text-xs text-gray-500">{d.summary}</span> : <span className="text-gray-400 text-[10px]">分析结论以详情为准</span>}
                </div>
                <div className="flex items-center gap-1.5">
                  {d.show_pnl && <span className={`text-xs font-semibold ${typeof d.pnl !== 'number' || !d.pnl_currency ? 'text-gray-400' : d.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {verifiedPnlText(d.pnl, d.pnl_currency)}
                  </span>}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    d.exec_status === '跟单成交已确认' ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-400'
                  }`}>{d.exec_status}</span>
                </div>
              </div>
              <button onClick={() => openDecision(d.id)} className="mt-3 text-xs font-medium text-indigo-600">
                查看详情
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  Main content (已登录)
// ═══════════════════════════════════════
function AgentContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabType = tabParam === 'decisions' || tabParam === 'my-copy' ? tabParam : 'agents';
  function setActiveTab(tab: TabType) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true, state: null });
  }

  const tabs = [
    { key: 'agents' as TabType, label: '智能体', icon: 'ri-robot-2-line' },
    { key: 'my-copy' as TabType, label: '我的跟单', icon: 'ri-link-m' },
    { key: 'decisions' as TabType, label: 'AI 决策', icon: 'ri-flashlight-line' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }} />
      <div className="px-5 pb-2">
        <h1 className="text-gray-900 font-bold text-xl">AI 跟单</h1>
      </div>

      {/* Segmented Control */}
      <div className="px-5 mt-3">
        <div className="flex bg-gray-100 rounded-xl p-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2.5 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                  isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400'
                }`}>
                <i className={`${tab.icon} ${isActive ? 'text-indigo-500' : ''}`} /> {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5 pt-4">
        {activeTab === 'agents' && <AgentsSection />}
        {activeTab === 'my-copy' && <MyCopySection />}
        {activeTab === 'decisions' && <DecisionsSection />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
//  Login prompt (未登录)
// ═══════════════════════════════════════
const AGENT_ROBOT_IMAGE = 'https://readdy.ai/api/search-image?query=cute%203D%20cartoon%20robot%20character%20with%20a%20small%20padlock%20badge%2C%20soft%20purple%20and%20blue%20gradient%20colors%2C%20minimalist%20clean%20design%2C%20rounded%20smooth%20shapes%2C%20friendly%20cute%20expression%2C%20light%20tech%20aesthetic%2C%20isolated%20on%20pure%20white%20background%2C%20centered%20composition%2C%20high%20quality%203D%20render%2C%20no%20text%2C%20no%20shadows&width=240&height=240&seq=agent_robot_lock_01&orientation=squarish';

function AgentLoginPrompt() {
  const navigate = useNavigate();
  const previewCards = [
    { icon: 'ri-robot-2-line', title: '浏览 AI 智能体', desc: '查看平台策略、历史表现、风险等级' },
    { icon: 'ri-link-m', title: '绑定交易账户跟单', desc: '一键绑定交易账户，自动同步 AI 决策' },
    { icon: 'ri-flashlight-line', title: 'AI 决策追踪', desc: '实时查看 AI 决策与执行记录' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="px-5 pb-4" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
        <h1 className="text-gray-900 font-bold text-xl">智能体</h1>
      </div>
      <LockedPrompt
        image={AGENT_ROBOT_IMAGE} title="登录后浏览 AI 智能体"
        subtitle="选择平台策略，绑定交易账户，开启智能跟单"
        buttonText="登录 / 注册" onButtonClick={() => navigate('/auth', { state: { from: '/agent' } })}
        secondaryAction={{ label: '先浏览智能体', onClick: () => navigate('/agent') }}>
        <div className="w-full flex flex-col gap-2.5">
          {previewCards.map((card) => (
            <div key={card.title} className="bg-white rounded-2xl p-3.5 relative" style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.05)' }}>
              <i className="ri-lock-line absolute top-3 right-3 text-gray-300 text-xs" />
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: '#EEF2FF' }}>
                  <i className={`${card.icon} text-base`} style={{ color: '#6366F1' }} />
                </div>
                <div className="min-w-0">
                  <p className="text-gray-900 font-semibold text-sm">{card.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{card.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </LockedPrompt>
    </div>
  );
}

export default function AgentPage() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <AgentLoginPrompt />;
  return <AgentContent />;
}
